import useResponsiveContent, { buildI18KeyResponsive } from '@source/hooks/useResponsiveContent';
import { TRegionKey } from '@source/interface';
import { FC } from 'react';
import { Trans } from 'next-i18next';
import styled from 'styled-components';

interface BannerHeadlineProps {
  region: TRegionKey;
  title: string;
  desc: string;
}

type FontSizeConfig = {
  [key: string]: {
    xs: [number, number];
    sm: [number, number];
    md: [number, number];
    lg: [number, number];
    xl: [number, number];
  };
};

const titleFontSizeConfig: FontSizeConfig = {
  jp: { xs: [20, 32], sm: [20, 32], md: [28, 32], lg: [28, 32], xl: [48, 48] },
  my: { xs: [24, 32], sm: [24, 32], md: [32, 40], lg: [40, 48], xl: [48, 56] },
};

const descFontSizeConfig: FontSizeConfig = {
  jp: { xs: [12, 16], sm: [12, 16], md: [12, 16], lg: [12, 16], xl: [22, 29] },
  my: { xs: [14, 20], sm: [14, 20], md: [16, 24], lg: [18, 28], xl: [22, 32] },
};

const getHeadLineContentConfig = ({ region }: { region: Partial<TRegionKey> }) => {
  return {
    title: titleFontSizeConfig[region],
    desc: descFontSizeConfig[region],
  };
};

const StyledBannerHeadline = styled.div<{ region: Partial<TRegionKey> }>`
  text-align: center;

  @media only screen and (min-width: ${(props) => props.theme.metrics.breakpoints.lg}) {
    text-align: left;
  }

  .title {
    ${(props) => props.theme.typo.familyGoogle.bold};
    font-size: ${({ region }) => getHeadLineContentConfig({ region }).title.xs[0]}px;
    line-height: ${({ region }) => getHeadLineContentConfig({ region }).title.xs[1]}px;
    color: #ffb799;
  }

  .desc {
    ${(props) => props.theme.typo.familyGoogle.medium};
    font-size: ${({ region }) => getHeadLineContentConfig({ region }).desc.xs[0]}px;
    line-height: ${({ region }) => getHeadLineContentConfig({ region }).desc.xs[1]}px;
    color: ${({ theme }) => theme.color.onPrimary};
    margin-top: 2px;
  }

  @media only screen and (min-width: ${(props) => props.theme.metrics.breakpoints.sm}) {
    .title {
      font-size: ${({ region }) => getHeadLineContentConfig({ region }).title.sm[0]}px;
      line-height: ${({ region }) => getHeadLineContentConfig({ region }).title.sm[1]}px;
    }
    .desc {
      font-size: ${({ region }) => getHeadLineContentConfig({ region }).desc.sm[0]}px;
      line-height: ${({ region }) => getHeadLineContentConfig({ region }).desc.sm[1]}px;
    }
  }

  @media only screen and (min-width: ${(props) => props.theme.metrics.breakpoints.md}) {
    .title {
      font-size: ${({ region }) => getHeadLineContentConfig({ region }).title.md[0]}px;
      line-height: ${({ region }) => getHeadLineContentConfig({ region }).title.md[1]}px;
    }
    .desc {
      font-size: ${({ region }) => getHeadLineContentConfig({ region }).desc.md[0]}px;
      line-height: ${({ region }) => getHeadLineContentConfig({ region }).desc.md[1]}px;
      margin-top: 11px;
    }
  }

  @media only screen and (min-width: ${(props) => props.theme.metrics.breakpoints.lg}) {
    .title {
      font-size: ${({ region }) => getHeadLineContentConfig({ region }).title.lg[0]}px;
      line-height: ${({ region }) => getHeadLineContentConfig({ region }).title.lg[1]}px;
    }
    .desc {
      font-size: ${({ region }) => getHeadLineContentConfig({ region }).desc.lg[0]}px;
      line-height: ${({ region }) => getHeadLineContentConfig({ region }).desc.lg[1]}px;
    }
  }

  @media only screen and (min-width: ${(props) => props.theme.metrics.breakpoints.xl}) {
    .title {
      font-size: ${({ region }) => getHeadLineContentConfig({ region }).title.xl[0]}px;
      line-height: ${({ region }) => getHeadLineContentConfig({ region }).title.xl[1]}px;
    }
    .desc {
      font-size: ${({ region }) => getHeadLineContentConfig({ region }).desc.xl[0]}px;
      line-height: ${({ region }) => getHeadLineContentConfig({ region }).desc.xl[1]}px;
      margin-top: 21px;
    }
  }
`;

const BannerHeadline: FC<BannerHeadlineProps> = ({ region, title, desc }) => {
  const tTitle = useResponsiveContent(buildI18KeyResponsive(title, ['xs', 'md', 'lg']));
  const tDesc = useResponsiveContent(buildI18KeyResponsive(desc, ['xs', 'md', 'lg']));

  return (
    <StyledBannerHeadline region={region}>
      {title && (
        <h1 className="title">
          <Trans ns="sellCar" i18nKey={tTitle} defaults={title} />
        </h1>
      )}
      {desc && (
        <div className="desc">
          <Trans ns="sellCar" i18nKey={tDesc} defaults={desc} />
        </div>
      )}
    </StyledBannerHeadline>
  );
};

export default BannerHeadline;
